import { XIcon } from 'lucide-react';
import * as React from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { ErrorState } from '../errors/error-state';
import { FilterChipLabel, useFilterChipSummary } from '../index-table/data-table-filter-chip';
import type { FilterDef, FilterValue } from '../index-table/types';

import { ChildRow, ItemRow, RowBody } from './resource-picker-rows';
import type { PickedChild, PickerRow, PickerSearchBy } from './types';
import type { UsePickerSelectionResult } from './use-picker-selection';

export interface ResourcePickerSelectedProps<TItem, TChild> {
  selection: UsePickerSelectionResult<TItem, TChild>;
  multiple: boolean | number;
  variantMode: boolean;
  getRow: (item: TItem) => PickerRow;
  /** Variant mode: a chosen child's row. */
  getChildRow?: ((picked: PickedChild<TItem, TChild>) => PickerRow) | undefined;
  /** A row the picker has: loaded in the list, picked from it, or fetched by id. */
  lookup: (id: string) => TItem | undefined;
  /** Variant mode: a chosen child that loaded, for initial child ids. */
  lookupChild: (childId: string) => PickedChild<TItem, TChild> | undefined;
  /** How many ids of each list are shown; "Show more" adds a batch. */
  window: number;
  onShowMore: () => void;
  /** Rows of the window are being fetched by id. */
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  filterDefs: ReadonlyArray<FilterDef<never>>;
  searchBy: PickerSearchBy | undefined;
  label: string;
}

/**
 * The "Selected (n)" tab. Id mode lists the picked rows with their checkboxes (unticking drops
 * one); rows that never loaded are fetched by id or counted as "n more not shown". Query mode
 * shows the query as read-only chips with its count, and the ids added to it and taken out of
 * it, each removable.
 */
export function ResourcePickerSelected<TItem, TChild>({
  listRef,
  ...props
}: ResourcePickerSelectedProps<TItem, TChild> & {
  /** Row controls are found by the arrow keys through this list. */
  listRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={listRef}
      className="min-h-0 flex-1 overflow-y-auto"
      role="group"
      aria-label={props.label}
    >
      {props.selection.allMatching ? (
        <QuerySelected {...props} />
      ) : props.variantMode ? (
        <ChildrenSelected {...props} />
      ) : (
        <IdsSelected {...props} />
      )}
    </div>
  );
}

function IdsSelected<TItem, TChild>({
  selection,
  multiple,
  getRow,
  lookup,
  window,
  onShowMore,
  loading,
  error,
  onRetry,
}: ResourcePickerSelectedProps<TItem, TChild>) {
  const shown = selection.picked.slice(0, window);
  const rows = shown.flatMap(([id, picked]) => {
    const item = picked ?? lookup(id);
    return item === undefined ? [] : [{ id, item }];
  });
  const unresolved = shown.length - rows.length;
  return (
    <>
      <ul className="divide-y">
        {rows.map(({ id, item }) => (
          <ItemRow
            key={id}
            id={id}
            item={item}
            row={getRow(item)}
            selection={selection}
            multiple={multiple}
          />
        ))}
      </ul>
      <SelectedFooter
        loading={loading}
        unresolved={unresolved}
        more={selection.picked.length - shown.length}
        error={error}
        onRetry={onRetry}
        onShowMore={onShowMore}
      />
    </>
  );
}

function ChildrenSelected<TItem, TChild>({
  selection,
  getChildRow,
  lookupChild,
  window,
  onShowMore,
}: ResourcePickerSelectedProps<TItem, TChild>) {
  const shown = selection.pickedChildren.slice(0, window);
  const rows = shown.flatMap(([id, picked]) => {
    const resolved = picked ?? lookupChild(id);
    return resolved ? [resolved] : [];
  });
  return (
    <>
      <ul className="divide-y">
        {rows.map((picked) => (
          <ChildRow
            key={picked.id}
            childId={picked.id}
            row={getChildRow?.(picked) ?? { title: picked.id }}
            checked
            full={false}
            onToggle={(checked) =>
              selection.toggleChildren(
                picked.itemId,
                picked.item,
                [{ id: picked.id, child: picked.child }],
                checked,
              )
            }
          />
        ))}
      </ul>
      <SelectedFooter
        loading={false}
        unresolved={shown.length - rows.length}
        more={selection.pickedChildren.length - shown.length}
        error={null}
        onRetry={onShowMore}
        onShowMore={onShowMore}
      />
    </>
  );
}

function SelectedFooter({
  loading,
  unresolved,
  more,
  error,
  onRetry,
  onShowMore,
}: {
  loading: boolean;
  unresolved: number;
  more: number;
  error: unknown;
  onRetry: () => void;
  onShowMore: () => void;
}) {
  const { t, i18n } = useTranslation('common');
  const number = new Intl.NumberFormat(i18n.language);
  if (loading) {
    return (
      <div className="flex flex-col gap-2 px-4 py-3" aria-hidden>
        <Skeleton className="h-10" />
        <Skeleton className="h-10" />
      </div>
    );
  }
  return (
    <div className="flex flex-col items-start gap-2 px-4 py-3 empty:hidden">
      {error ? <ErrorState error={error} onRetry={onRetry} compact /> : null}
      {unresolved > 0 && !error ? (
        <p className="text-muted-foreground text-sm">
          {t('resourcePicker.notShown', { number: number.format(unresolved) })}
        </p>
      ) : null}
      {more > 0 ? (
        <Button type="button" variant="ghost" size="sm" onClick={onShowMore}>
          {t('resourcePicker.showMore', { number: number.format(more) })}
        </Button>
      ) : null}
    </div>
  );
}

const CHIP =
  'inline-flex h-7 max-w-full min-w-0 items-center gap-1 rounded-md bg-muted px-2 text-sm';

function ReadOnlyChip({ def, value }: { def: FilterDef<never>; value: FilterValue }) {
  const summary = useFilterChipSummary(def, value);
  return (
    <li className={CHIP} title={summary?.text}>
      <FilterChipLabel def={def} summary={summary} />
    </li>
  );
}

function QuerySelected<TItem, TChild>({
  selection,
  getRow,
  lookup,
  window,
  onShowMore,
  loading,
  error,
  onRetry,
  filterDefs,
  searchBy,
}: ResourcePickerSelectedProps<TItem, TChild>) {
  const { t, i18n } = useTranslation('common');
  const number = new Intl.NumberFormat(i18n.language);
  const headingId = React.useId();
  const all = selection.allMatching;
  if (!all) return null;
  const { q, filters } = all.query;
  const applied = filterDefs.flatMap((def) => {
    const value = filters[def.key];
    return value ? [{ def, value }] : [];
  });
  const by =
    searchBy?.options.find((option) => option.value === all.query.searchBy)?.label ??
    t('resourcePicker.search');
  const rowOf = (id: string): PickerRow => {
    const item = lookup(id);
    return item === undefined ? { title: id } : { ...getRow(item), disabled: false };
  };
  const included = selection.includeIds.slice(0, window);
  const excluded = selection.excludeIds.slice(0, window);
  const more =
    selection.includeIds.length - included.length + selection.excludeIds.length - excluded.length;

  return (
    <div className="flex flex-col gap-4 px-4 py-3">
      <section aria-labelledby={headingId} className="flex flex-col gap-2">
        <h3 id={headingId} className="text-sm font-medium">
          {t('resourcePicker.queryHeading')}
        </h3>
        {q || applied.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5" aria-label={t('resourcePicker.queryFilters')}>
            {q ? <li className={CHIP}>{t('resourcePicker.searchChip', { by, q })}</li> : null}
            {applied.map(({ def, value }) => (
              <ReadOnlyChip key={def.key} def={def} value={value} />
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">{t('resourcePicker.noFilters')}</p>
        )}
        <p className="text-sm">
          {t('resourcePicker.queryCount', {
            total: number.format(all.total),
            excluded: number.format(selection.excludeIds.length),
            included: number.format(selection.includeIds.length),
          })}
        </p>
        <div>
          <Button type="button" variant="outline" size="sm" onClick={selection.leaveAllMatching}>
            {t('resourcePicker.deselectAllMatching')}
          </Button>
        </div>
      </section>
      {included.length > 0 ? (
        <IdList
          heading={t('resourcePicker.includedHeading', {
            number: number.format(selection.includeIds.length),
          })}
          ids={included}
          rowOf={rowOf}
          actionLabel={(title) => t('resourcePicker.remove', { title })}
          action={<XIcon aria-hidden />}
          onAction={(id) => selection.removeIds([id])}
        />
      ) : null}
      {excluded.length > 0 ? (
        <IdList
          heading={t('resourcePicker.excludedHeading', {
            number: number.format(selection.excludeIds.length),
          })}
          ids={excluded}
          rowOf={rowOf}
          actionLabel={(title) => t('resourcePicker.restore', { title })}
          action={t('resourcePicker.restoreShort')}
          onAction={selection.unexclude}
        />
      ) : null}
      {loading || error || more > 0 ? (
        <SelectedFooter
          loading={loading}
          unresolved={0}
          more={more}
          error={error}
          onRetry={onRetry}
          onShowMore={onShowMore}
        />
      ) : null}
    </div>
  );
}

function IdList({
  heading,
  ids,
  rowOf,
  actionLabel,
  action,
  onAction,
}: {
  heading: string;
  ids: readonly string[];
  rowOf: (id: string) => PickerRow;
  actionLabel: (title: string) => string;
  action: React.ReactNode;
  onAction: (id: string) => void;
}) {
  const headingId = React.useId();
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-1">
      <h3 id={headingId} className="text-sm font-medium">
        {heading}
      </h3>
      <ul className="divide-y rounded-md border">
        {ids.map((id) => {
          const row = rowOf(id);
          return (
            <li key={id} className="flex min-h-12 items-center gap-3 px-3 py-1.5 text-sm">
              <RowBody row={{ ...row, meta: undefined }} />
              <Button
                type="button"
                variant="ghost"
                size={typeof action === 'string' ? 'sm' : 'icon'}
                className={typeof action === 'string' ? 'h-7' : 'size-7 shrink-0'}
                aria-label={actionLabel(row.title)}
                onClick={() => onAction(id)}
              >
                {action}
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
